import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, Lock, CheckCircle2, XCircle, Clock, Target, Brain, 
  TrendingUp, AlertTriangle, FileText, ArrowRight, ArrowLeft,
  Calendar, BookOpen, ChevronRight, Lightbulb, Shield, Zap,
  BarChart3, Filter
} from "lucide-react";

const PYQPapersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [yearsData, setYearsData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedPaper, setSelectedPaper] = useState("gs1");
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [stats, setStats] = useState(null);
  const [subjectFilter, setSubjectFilter] = useState(null);

  const isPro = user?.role === "PRO";

  // Fetch available years
  const fetchYears = useCallback(async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/pyq-papers/years`);
      setYearsData(data);
    } catch (err) {
      toast({ variant: "destructive", description: "Failed to load PYQ years" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch user stats
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/pyq-papers/stats`);
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchYears();
    fetchStats();
  }, [fetchYears, fetchStats]);

  // Fetch questions for selected year/paper
  const fetchQuestions = async (year, paper) => {
    setLoading(true);
    try {
      // For 2020-2025, fetch all 100 actual UPSC questions
      const questionLimit = year >= 2020 ? 100 : 50;
      const { data } = await managedAxios.post(`${API_BASE}/pyq-papers/get-questions`, {
        year,
        paper,
        subject_filter: subjectFilter,
        limit: questionLimit,
        offset: 0
      });
      setQuestions(data.questions);
      setSelectedYear(year);
      setSelectedPaper(paper);
    } catch (err) {
      toast({ variant: "destructive", description: "Failed to load questions" });
    } finally {
      setLoading(false);
    }
  };

  // Attempt a question
  const handleAttempt = async (questionId, answer) => {
    setSelectedAnswer(answer);
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/pyq-papers/attempt`, {
        question_id: questionId,
        user_answer: answer
      }, {
        timeout: 60000 // 60 second timeout for AI explanation generation
      });
      setExplanation(data);
      setShowExplanation(true);
      fetchStats(); // Refresh stats
    } catch (err) {
      console.error("Attempt error:", err);
      toast({ variant: "destructive", description: "Failed to submit answer. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Start question
  const startQuestion = (question) => {
    setCurrentQuestion(question);
    setShowExplanation(false);
    setExplanation(null);
    setSelectedAnswer(null);
  };

  // Loading state
  if (loading && !yearsData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Question Practice View
  if (currentQuestion) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Button variant="outline" onClick={() => setCurrentQuestion(null)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Questions
        </Button>

        {/* Question Card — keyed by question id so it fully remounts on
            navigation, preventing Google Translate stale option text. */}
        <Card key={currentQuestion.question_id} className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-blue-100 text-blue-700">
                {currentQuestion.year} - {currentQuestion.paper}
              </Badge>
              <Badge variant="outline">{currentQuestion.subject}</Badge>
              {currentQuestion.difficulty && (
                <Badge variant="outline" className={
                  currentQuestion.difficulty === "hard" ? "border-red-300 text-red-700" :
                  currentQuestion.difficulty === "medium" ? "border-yellow-300 text-yellow-700" :
                  "border-green-300 text-green-700"
                }>
                  {currentQuestion.difficulty}
                </Badge>
              )}
              {currentQuestion.question_type && (
                <Badge variant="outline" className="text-xs">
                  {currentQuestion.question_type.replace(/_/g, " ")}
                </Badge>
              )}
            </div>

            <p className="text-lg text-slate-900 mb-6 leading-relaxed">
              {currentQuestion.question}
            </p>

            {/* Options */}
            <div className="space-y-3">
              {Object.entries(currentQuestion.options || {}).map(([key, value]) => {
                let optionStyle = "border-slate-200 hover:border-blue-300 hover:bg-slate-50";
                
                if (showExplanation) {
                  if (key === explanation?.correct_answer) {
                    optionStyle = "border-green-500 bg-green-50";
                  } else if (key === selectedAnswer && key !== explanation?.correct_answer) {
                    optionStyle = "border-red-500 bg-red-50";
                  }
                } else if (selectedAnswer === key) {
                  optionStyle = "border-blue-500 bg-blue-50";
                }

                return (
                  <button
                    key={key}
                    onClick={() => !showExplanation && setSelectedAnswer(key)}
                    disabled={showExplanation}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${optionStyle}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        showExplanation && key === explanation?.correct_answer
                          ? "bg-green-500 text-white"
                          : showExplanation && key === selectedAnswer && key !== explanation?.correct_answer
                          ? "bg-red-500 text-white"
                          : selectedAnswer === key
                          ? "bg-blue-500 text-white"
                          : "bg-slate-200 text-slate-700"
                      }`}>
                        {key}
                      </span>
                      <span className="text-slate-700">{value}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Submit Button - Always visible until explanation is shown */}
            {!showExplanation && (
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={() => handleAttempt(currentQuestion.question_id, selectedAnswer)}
                  disabled={!selectedAnswer || loading}
                  className="px-8 bg-blue-600 hover:bg-blue-700 text-white"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating AI Explanation...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Check Answer
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Show selected answer indicator */}
            {selectedAnswer && !showExplanation && (
              <p className="text-center mt-2 text-sm text-slate-500">
                You selected: <strong className="text-blue-600">{selectedAnswer}</strong>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Explanation Card */}
        {showExplanation && explanation && (
          <Card className="mb-6">
            <CardContent className="p-6">
              {/* Result Banner */}
              <div className={`p-4 rounded-lg mb-6 ${
                explanation.is_correct ? "bg-green-100" : "bg-red-100"
              }`}>
                <div className="flex items-center gap-3">
                  {explanation.is_correct ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  <span className={`font-bold ${
                    explanation.is_correct ? "text-green-800" : "text-red-800"
                  }`}>
                    {explanation.is_correct ? "Correct!" : "Incorrect"}
                  </span>
                  <span className="text-slate-600">
                    Correct answer: <strong>{explanation.correct_answer}</strong>
                  </span>
                </div>
              </div>

              {/* PRO Explanation */}
              {isPro && explanation.why_correct && (
                <>
                  {/* Why Correct */}
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      Why This Answer is Correct
                    </h3>
                    <p className="text-slate-700">{explanation.why_correct}</p>
                  </div>

                  {/* Elimination Logic */}
                  {explanation.elimination_logic && (
                    <div className="mb-6">
                      <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5 text-purple-600" />
                        Elimination Logic
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(explanation.elimination_logic).map(([key, value]) => (
                          <div key={key} className={`p-3 rounded-lg ${
                            key === explanation.correct_answer ? "bg-green-50 border border-green-200" : "bg-slate-50"
                          }`}>
                            <span className="font-bold text-slate-700 mr-2">{key}:</span>
                            <span className="text-slate-600">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Behaviour Insights Row */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {/* Trap Identification */}
                    {explanation.trap_identification && (
                      <Card className="border-orange-200 bg-orange-50">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-orange-900 mb-1 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Trap Identification
                          </h4>
                          <p className="text-sm text-orange-800">{explanation.trap_identification}</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Attempt/Skip Advice */}
                    {explanation.attempt_skip_advice && (
                      <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Attempt/Skip Advice
                          </h4>
                          <p className="text-sm text-blue-800">{explanation.attempt_skip_advice}</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Time Risk */}
                    {explanation.time_risk && (
                      <Card className="border-purple-200 bg-purple-50">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-purple-900 mb-1 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Time Risk
                          </h4>
                          <p className="text-sm text-purple-800">{explanation.time_risk}</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Behaviour Insight */}
                    {explanation.behaviour_insight && (
                      <Card className="border-green-200 bg-green-50">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-green-900 mb-1 flex items-center gap-2">
                            <Brain className="w-4 h-4" />
                            Behaviour Insight
                          </h4>
                          <p className="text-sm text-green-800">{explanation.behaviour_insight}</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* PYQ Pattern */}
                  {explanation.pyq_pattern && (
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">PYQ Pattern: </span>
                      <span className="text-sm text-slate-600">{explanation.pyq_pattern}</span>
                      {explanation.similar_years && (
                        <span className="text-sm text-slate-500 ml-2">
                          (Similar: {explanation.similar_years})
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* FREE User Limited Explanation */}
              {!isPro && (
                <>
                  {explanation.short_explanation && (
                    <div className="mb-4">
                      <h3 className="font-bold text-slate-900 mb-2">Brief Explanation</h3>
                      <p className="text-slate-700">{explanation.short_explanation}</p>
                    </div>
                  )}

                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-900 font-medium mb-2">{explanation.upgrade_message}</p>
                          <p className="text-sm text-amber-700 mb-3">PRO unlocks:</p>
                          <ul className="text-sm text-amber-700 space-y-1">
                            {explanation.locked_features?.map((feature, idx) => (
                              <li key={idx}>• {feature}</li>
                            ))}
                          </ul>
                          <Button 
                            className="mt-4 bg-amber-500 hover:bg-amber-600"
                            onClick={() => navigate('/upgrade')}
                          >
                            Upgrade to PRO
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Question Button */}
        {showExplanation && (
          <div className="flex justify-center">
            <Button onClick={() => {
              const currentIdx = questions.findIndex(q => q.question_id === currentQuestion.question_id);
              if (currentIdx < questions.length - 1) {
                startQuestion(questions[currentIdx + 1]);
              } else {
                setCurrentQuestion(null);
              }
            }}>
              {questions.findIndex(q => q.question_id === currentQuestion.question_id) < questions.length - 1 
                ? "Next Question" 
                : "Back to Questions"
              }
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Questions List View
  if (selectedYear && questions.length > 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Button variant="outline" onClick={() => {
          setSelectedYear(null);
          setQuestions([]);
        }} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Year Selection
        </Button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            UPSC {selectedYear} - {selectedPaper === "gs1" ? "GS Paper I" : "CSAT"}
          </h1>
          <p className="text-slate-600">{questions.length} questions available</p>
        </div>

        {/* Questions Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {questions.map((q, idx) => (
            <Card key={q.question_id} className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => startQuestion(q)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="outline" className="text-xs">Q{idx + 1}</Badge>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-xs">{q.subject}</Badge>
                    {q.difficulty && (
                      <Badge variant="outline" className={`text-xs ${
                        q.difficulty === "hard" ? "border-red-300" :
                        q.difficulty === "medium" ? "border-yellow-300" : "border-green-300"
                      }`}>
                        {q.difficulty}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-slate-700 text-sm line-clamp-3">{q.question}</p>
                <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                  Practice <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Main Year Selection View
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">PYQ Papers</h1>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Practice actual UPSC Prelims questions from previous years.<br/>
          <strong className="text-slate-800">Understand patterns. Master elimination. Clear Prelims.</strong>
        </p>
        <p className="text-xs text-slate-500 mt-2 italic">
          {yearsData?.disclaimer}
        </p>
      </div>

      {/* User Stats */}
      {stats && stats.overall.total_attempted > 0 && (
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Your PYQ Progress
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.overall.total_attempted}</p>
                <p className="text-sm text-slate-600">Questions Practiced</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{stats.overall.total_correct}</p>
                <p className="text-sm text-slate-600">Correct</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{stats.overall.accuracy}%</p>
                <p className="text-sm text-slate-600">Accuracy</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paper Selection */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Select Paper</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {yearsData?.papers?.map(paper => (
            <Card 
              key={paper.id}
              className={`cursor-pointer transition-all ${
                selectedPaper === paper.id 
                  ? "ring-2 ring-blue-500 border-blue-300" 
                  : "hover:border-blue-300"
              }`}
              onClick={() => setSelectedPaper(paper.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">{paper.short_name}</h3>
                    <p className="text-sm text-slate-600">{paper.name}</p>
                  </div>
                  <Badge className={selectedPaper === paper.id ? "bg-blue-500" : "bg-slate-200 text-slate-700"}>
                    {selectedPaper === paper.id ? "Selected" : "Select"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Year Selection */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Select Year</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {yearsData?.years?.map(year => (
            <Card 
              key={year}
              className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
              onClick={() => fetchQuestions(year, selectedPaper)}
            >
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="font-bold text-slate-900">{year}</span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  View Questions →
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* FREE User Message */}
      {!isPro && (
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-amber-900 font-medium">
                  🔒 FREE users get basic explanations. PRO unlocks full elimination logic and behaviour insights.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-100"
                  onClick={() => navigate('/upgrade')}
                >
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PYQPapersPage;
